// =====================================================================
// REQUEST TYPES
// Shared between frontend and backend
// =====================================================================

import { Folder } from "./folder";
import type {
  RequestTemplate,
  SignatureTemplateSigner,
} from "./request-template";

// Enums (match database enums)
export enum RequestStatus {
  PENDING = "pending",
  SENT = "sent",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  CANCELED = "canceled",
}

export enum ParticipantRole {
  RESPONDER = "responder",
  SIGNER = "signer",
}

export enum ParticipantStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  DECLINED = "declined",
  FAILED = "failed",
  REPLACED = "replaced",
}

export enum ReminderTiming {
  BEFORE_DUE = "before_due",
  ON_DUE = "on_due",
  AFTER_DUE = "after_due",
}

export enum ReminderStatus {
  PENDING = "pending",
  SENT = "sent",
  FAILED = "failed",
}

export enum EnvelopeStatus {
  CREATED = "created",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELED = "canceled",
}

export enum FileSource {
  MANUAL = "manual",
  FORM = "form",
  SIGNATURE = "signature",
}

// Domain types
export type RequestParticipant = {
  id: string;
  request_id: string;
  role: ParticipantRole;
  template_signer_id: string | null;
  email: string;
  name: string | null;
  status: ParticipantStatus;
  envelope_recipient_id: string | null;
  token: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  // Relations
  template_signer?: SignatureTemplateSigner;
  participant_reminders?: ParticipantReminder[];
};

export type Request = {
  id: string;
  template_id: string;
  case_id: string;
  status: RequestStatus;
  due_date: string | null;
  output_folder_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
  // Relations (match Prisma relation names)
  template?: RequestTemplate;
  request_participants: RequestParticipant[];
  output_folder?: Folder;
  envelopes?: Envelope[];
};

export type ParticipantReminder = {
  id: string;
  participant_id: string;
  timing: ReminderTiming;
  offset_days: number;
  scheduled_at: string;
  sent_at: string | null;
  status: ReminderStatus;
  created_at: string;
  updated_at: string;
};

export type Envelope = {
  id: string;
  organization_id: string;
  request_id: string;
  envelope_id: string;
  envelope_status: EnvelopeStatus;
  signer_count: number;
  cost_cents: number;
  charged_cents: number | null;
  deliverable_file_id: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  metadata: Record<string, unknown>;
};

// DTOs
export type CreateRequestParticipantDto = {
  email: string;
  name?: string;
};

export type CreateReminderDto = {
  timing: ReminderTiming;
  offset_days: number;
};

export type CreateRequestDto = {
  template_id: string;
  case_id: string;
  due_date?: string;
  output_folder_id?: string;
  participants: CreateRequestParticipantDto[];
  reminders?: CreateReminderDto[];
};

// Alias for backwards compatibility - UI and backend now use the same participant structure
export type CreateRequestUiDto = CreateRequestDto;

export type UpdateRequestStatusDto = {
  status: RequestStatus;
};

export type UpdateRequestDto = {
  due_date?: string | null;
  output_folder_id?: string | null;
};

export type ParticipantByTokenDto = {
  id: string;
  email: string;
  request: {
    id: string;
    due_date: string | null;
    output_folder_id: string | null;
    created_at: string;
    requestor: {
      email: string;
      full_name: string | null;
    } | null;
    template: {
      form_template_fields: any;
      title: string;
      description: string;
      instructions: string | null;
    };
  };
};

// Form Submission types
export type FormSubmissionFieldValue = {
  key: string;
  label: string;
  type: string;
  value: string | { file_id: string; filename: string } | string[];
};

export type FormSubmissionData = {
  fields: FormSubmissionFieldValue[];
  submission_version: number;
  notes?: string;
};

export type FormSubmission = {
  id: string;
  request_id: string;
  case_id: string;
  submitted_by: string | null;
  organization_id: string;
  encrypted_field_values: string;
  encrypted_dek: string;
  kms_key_id: string;
  encryption_algorithm: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};
